import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Table, Header, Icon } from "semantic-ui-react";
import classNames from "classnames";
import moment from "moment";
import styles from "./TableRow.module.css";
import Loader from "../../../../../components/Loader";
import Numeric from "../../../../../components/Numeric";
import { fetchPieceRatePayrollsByEmployeeId } from "../../../../../actions/PieceRatePayroll/pieceRatePayroll";
import { minutesToHM } from "../../../../../utils/timeUtils";

const TableRow = ({
  isStriped,
  location,
  payroll: {
    employeeId,
    employeeName,
    contractor,
    workingSeconds,
    numberOfPieces,
    totalPay,
    expandedData
  },
  dateFrom,
  dateTo
}) => {
  const dispatch = useDispatch();
  const [showExpandedData, setShowExpandedData] = useState(false);

  useEffect(() => {
    if (showExpandedData && !expandedData) {
      dispatch(
        fetchPieceRatePayrollsByEmployeeId({
          employeeId,
          from: dateFrom,
          to: dateTo
        })
      );
    }
  }, [showExpandedData]);

  useEffect(() => {
    setShowExpandedData(false);
  }, [location.hash]);

  const onExpandClick = () => {
    setShowExpandedData(!showExpandedData);
  };

  const formTaskLink = taskId => () => {
    const [type, id] = taskId.split("-");

    switch (type) {
      case "GT":
        return `/tasks//${id}/#sort=id,desc&ids=${id}`;
      case "ST":
        return `/spray_diary/id=${id}/${id}#sort=date,desc`;
      default:
        return "/";
    }
  };

  return (
    <>
      <Table.Row
        className={classNames({
          [styles.stripedRow]: isStriped,
          [styles.expandedRow]: showExpandedData
        })}
      >
        <Table.Cell
          className={classNames(styles.iconCell, {
            [styles.drawGreenLine]: showExpandedData
          })}
        >
          <Icon
            className={classNames(styles.toggleIcon, "tuf-chevron-down", {
              [styles.toggleIconTurned]: showExpandedData
            })}
            onClick={onExpandClick}
          />
        </Table.Cell>
        <Table.Cell>{employeeName}</Table.Cell>
        <Table.Cell>{contractor || "—"}</Table.Cell>
        <Table.Cell>{minutesToHM(workingSeconds / 60)}</Table.Cell>
        <Table.Cell>
          <Numeric
            value={numberOfPieces}
            fractionDigits={2}
            commaSeparatorOnThousands
          />
        </Table.Cell>
        <Table.Cell>
          <Numeric
            value={totalPay}
            fractionDigits={2}
            commaSeparatorOnThousands
            units="$ "
            unitsPosition="left"
          />
        </Table.Cell>
      </Table.Row>
      {showExpandedData && expandedData && (
        <Table.Row
          className={classNames(styles.additionalRow, styles.expandedRow)}
        >
          <Table.Cell className={styles.drawGreenLine} />
          <Table.Cell colSpan={5}>
            <Table
              basic="very"
              unstackable
              className={styles.additionalInfoTable}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Task ID</Table.HeaderCell>
                  <Table.HeaderCell>Task type</Table.HeaderCell>
                  <Table.HeaderCell>Working time</Table.HeaderCell>
                  <Table.HeaderCell>Unit type</Table.HeaderCell>
                  <Table.HeaderCell>Number of pieces</Table.HeaderCell>
                  <Table.HeaderCell>Rate($/piece)</Table.HeaderCell>
                  <Table.HeaderCell>Total Pay</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {expandedData.isFetching ? (
                  <Table.Row>
                    <Table.Cell>
                      <Loader className={styles.loader} />
                    </Table.Cell>
                  </Table.Row>
                ) : expandedData.items.length > 0 ? (
                  expandedData.items.map(
                    ({
                      date,
                      taskId,
                      taskType,
                      workingSeconds,
                      unitType,
                      numberOfPieces,
                      rate
                    }) => (
                      <Table.Row key={`expanded_row_${taskId}`}>
                        <Table.Cell>
                          {moment(date).format("DD/MM/YYYY")}
                        </Table.Cell>
                        <Table.Cell>
                          {taskId ? (
                            <Link to={formTaskLink(taskId)} target="_blank">
                              {taskId}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </Table.Cell>
                        <Table.Cell>{taskType || "—"}</Table.Cell>
                        <Table.Cell>
                          {minutesToHM(workingSeconds / 60)}
                        </Table.Cell>
                        <Table.Cell>{unitType}</Table.Cell>
                        <Table.Cell>
                          <Numeric
                            value={numberOfPieces}
                            fractionDigits={2}
                            commaSeparatorOnThousands
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Numeric
                            value={rate}
                            fractionDigits={2}
                            commaSeparatorOnThousands
                            units="$ "
                            unitsPosition="left"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Numeric
                            value={numberOfPieces * rate}
                            fractionDigits={2}
                            commaSeparatorOnThousands
                            units="$ "
                            unitsPosition="left"
                          />
                        </Table.Cell>
                      </Table.Row>
                    )
                  )
                ) : (
                  <Header as="h4">Nothing found</Header>
                )}
              </Table.Body>
            </Table>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

TableRow.propTypes = {
  isStriped: PropTypes.bool,
  location: PropTypes.object.isRequired,
  payroll: PropTypes.shape({
    employeeId: PropTypes.string,
    employeeName: PropTypes.string,
    contractor: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ]),
    workingSeconds: PropTypes.number,
    numberOfPieces: PropTypes.number,
    totalPay: PropTypes.number,
    expandedData: PropTypes.shape({
      date: PropTypes.string,
      taskId: PropTypes.string,
      taskType: PropTypes.string,
      workingSeconds: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null])
      ]),
      unitType: PropTypes.string,
      numberOfPieces: PropTypes.number,
      rate: PropTypes.number
    })
  }),
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string
};

export default TableRow;
